/**
 * Public Contractor Registry directory (docs/design/contractor-registry/spec.md §3c/§4/§5) — NO
 * auth, same posture as concernsMap.ts/publicApi.ts's public reads. The server enforces the
 * `visibility == 'Public' AND status == 'Approved'` predicate on every response here
 * (handler.PublicContractorDirectory) — this module never re-filters or widens what comes back; it
 * only normalizes the nil-slice-marshals-as-JSON-null gotcha (`trades ?? []`) at the fetch choke
 * point, per the task brief's "render only what the API returns" instruction.
 */
import { apiFetch } from "./client.js";
import type { PublicContractorView } from "./types.js";

interface WirePublicContractorsResponse {
  contractors?: PublicContractorView[];
  count?: number;
}

/** Public trade-taxonomy entry — same shape as the staff CategoryView's id/label, minus the
 *  staff-only fields (department/order/active) a public endpoint has no business returning. */
export interface PublicTradeCategory {
  id: string;
  label: string;
}

interface WirePublicTradesResponse {
  trades?: PublicTradeCategory[];
}

/** GET /api/public/contractor-trades — the PUBLIC trade taxonomy (agreed contract with Inky's
 *  feat/public-contractor-trades-endpoint). Unlike listTradeCategoriesLive (staff-auth,
 *  Kind=trade Categories, used by the Contractors console), this is what the public directory
 *  must use for its per-trade filter pills + id→label resolution — the staff endpoint 401s for a
 *  resident with no session, which is exactly why the pills/labels were missing on prod. */
export async function listPublicTradeCategoriesLive(): Promise<PublicTradeCategory[]> {
  const res = await apiFetch<WirePublicTradesResponse>("/public/contractor-trades");
  return res.trades ?? [];
}

/** GET /api/public/contractors?trade=&q= — the directory list (spec §3c). Both the unfiltered
 *  default list and a name search/trade-filtered one hit this SAME endpoint/predicate (no separate
 *  "search" code path, per the acceptance criteria). */
export async function listPublicContractorsLive(
  trade?: string,
  q?: string,
): Promise<PublicContractorView[]> {
  const res = await apiFetch<WirePublicContractorsResponse>("/public/contractors", {
    query: { trade, q },
  });
  return (res.contractors ?? []).map((c) => ({ ...c, trades: c.trades ?? [] }));
}

/** GET /api/public/contractors/{id} — single lookup. The server returns 404 (ErrNotFound) for
 *  EVERY non-matching case (doesn't exist, Private, Pending, Suspended, Expired) — indistinguishable
 *  by design (spec §3c). This resolves to `null` for a 404 rather than throwing, so callers render
 *  the same "not currently listed" empty state regardless of WHY the lookup came back empty. */
export async function getPublicContractorLive(id: string): Promise<PublicContractorView | null> {
  try {
    const res = await apiFetch<{ contractor: PublicContractorView }>(
      `/public/contractors/${encodeURIComponent(id)}`,
    );
    return { ...res.contractor, trades: res.contractor.trades ?? [] };
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}
